import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import {
  documentTypeMap,
  loanTables,
} from "../../config/incentiveClaimFormUtils";
import { getStoredItem } from "../../config/storageUtils";
import { formatDate, scriptRegex, uploadDocumentToServer } from "../../utils/utils";
import api from "../../api/ApiService";
import { BASE_URL } from "../../config/apiConfig";
import ExependitureDetailInstruction from "../../assets/documents/ExependitureDetailInstruction.pdf";
import Message from "./Message";

const TableInput = ({
  divClass,
  label,
  htmlFor,
  tableClass,
  headerClass,
  headerBg,
  headers,
  rows,
  rowClass,
  namePrefix,
  onChange,
  errors = {},
  canAddMore = true,
  formData,
  setFormData,
  tableDisabled,
  language = false,
  applicationId = null,
}) => {
  const InvestorExpenditureSheet = "/documents/InvestorExpenditureSheet.xlsx";
  const [capitalExpenditureDataImported, setCapitalExpenditureDataImported] =
    useState(false);
  const isSpecialTable =
    htmlFor === "capital_expenditure_details_through_bank_transactions";
  const [expenditureExcelFileURL, setExpenditureExcelFileURL] = useState("");
  const [expenditurePdfFileURL, setExpenditurePdfFileURL] = useState("");
  const [apiMessage, setApiMessage] = useState(null);

  useEffect(() => {
    if (isSpecialTable && formData?.capital_expenditure_details_excel?.url) {
      setExpenditureExcelFileURL(
        formData?.capital_expenditure_details_excel?.url,
      );
    }
    if (isSpecialTable && formData?.capital_expenditure_details_pdf?.url) {
      setExpenditurePdfFileURL(formData?.capital_expenditure_details_pdf?.url);
    }
    if (
      isSpecialTable &&
      formData?.capital_expenditure_details_excel?.url &&
      formData?.capital_expenditure_details_pdf?.url
    ) {
      setCapitalExpenditureDataImported(true);
    }
  }, [isSpecialTable]);

  const handleInputChange = async (e, rowIndex, fieldName) => {
    const updatedRows = [...rows];
    let value = e.target.value;

    if (!scriptRegex.test(value)) {
      // ✅ If file input, store the file URL instead of the file object
      if (e.target.type === "file" && e.target.files?.[0]) {
        value = await uploadDocumentToServer(e.target.files[0]);
      }
      updatedRows[rowIndex] = {
        ...updatedRows[rowIndex],
        [fieldName]: value,
      };

      onChange(updatedRows, false, rowIndex, fieldName);
    }
  };

  const addRow = () => {
    const newRow = {};
    headers.forEach((header) => {
      newRow[header.name] = "";
    });
    onChange([...rows, newRow], true);
  };

  const removeRow = (rowIndex) => {
    const updatedRows = rows.filter((_, i) => i !== rowIndex);
    onChange(updatedRows, false, null, null, true);
  };

  // ✅ Download Excel with locked headers
  const downloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Template");

    // ✅ Header row (locked)
    const headerNames = headers.map((h) => h.label);
    const headerRow = sheet.addRow(headerNames);

    headerRow.eachCell((cell) => {
      cell.protection = { locked: true };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F497D" }, // dark blue background
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });

    // ✅ Add empty editable rows
    for (let i = 0; i < 10; i++) {
      const row = sheet.addRow(headers.map(() => ""));
      row.eachCell((cell) => {
        cell.protection = { locked: false }; // editable
      });
    }

    // ✅ Auto-fit column width based on header length
    sheet.columns.forEach((col, index) => {
      const headerLength = headerNames[index].length;
      col.width = headerLength < 20 ? 20 : headerLength + 5; // dynamic width
    });

    // ✅ Protect sheet
    await sheet.protect("investuk123", {
      selectLockedCells: true,
      selectUnlockedCells: true,
      deleteRows: true, // ✅ allow deleting rows
      insertRows: true, // ✅ allow inserting rows
      formatCells: true, // allow formatting of editable cells
    });

    // ✅ Export file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Capital_Expenditure_Template.xlsx");
  };

  const formatToYMD = (date) => {
    const d = new Date(date);
    if (isNaN(d)) return "";
    return d.toISOString().split("T")[0]; // yyyy-mm-dd
  };

  const findLastFilledRow = (sheet, startRow, colCount) => {
    let lastFilledRow = startRow - 1;

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber < startRow) return;

      const hasData = Array.from({ length: colCount }).some((_, i) => {
        const cell = row.getCell(i + 1).value;
        return (
          cell !== null && cell !== undefined && String(cell).trim() !== ""
        );
      });

      if (hasData) {
        lastFilledRow = rowNumber;
      }
    });

    return lastFilledRow;
  };

  // ✅ Import Excel back into table
  const importFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const sheet = workbook.worksheets[0];

    const HEADER_ROW_NUMBER = 4;
    const DATA_START_ROW = HEADER_ROW_NUMBER + 1;

    const expectedHeaders = headers.map((h) => h.label);

    // ✅ Read & normalize headers (only expected count)
    const importedHeaders = sheet
      .getRow(HEADER_ROW_NUMBER)
      .values.slice(1)
      .map((h) =>
        typeof h === "object" && h?.text ? h.text : String(h || "").trim(),
      )
      .slice(0, expectedHeaders.length);

    // ✅ Validate headers
    if (JSON.stringify(importedHeaders) !== JSON.stringify(expectedHeaders)) {
      alert("Invalid file headers. Please use the provided template.");
      return;
    }

    const newRows = [];
    const newErrors = {};
    const errorMessages = [];

    const lastFilledRow = findLastFilledRow(
      sheet,
      DATA_START_ROW,
      headers.length,
    );

    if (lastFilledRow < DATA_START_ROW) {
      alert("No data rows found in the file.");
      return;
    }
    let emptyRowStreak = 0;

    // ✅ Loop only through real data rows
    for (
      let rowNumber = DATA_START_ROW;
      rowNumber <= lastFilledRow;
      rowNumber++
    ) {
      const row = sheet.getRow(rowNumber);

      // ✅ Read only expected columns
      const rowValues = headers.map((_, i) => {
        const cell = row.getCell(i + 1).value;
        return cell && cell instanceof Date
          ? formatToYMD(cell)
          : typeof cell === "object"
            ? cell?.text || cell?.result || ""
            : String(cell || "").trim();
      });

      // ✅ Skip fully empty rows
      if (rowValues.every((val) => val === "")) {
        emptyRowStreak++;
        // stop if 5 continuous empty rows (Excel scroll garbage guard)
        if (emptyRowStreak >= 5) break;
        continue;
      }
      emptyRowStreak = 0;

      const rowData = {};

      headers.forEach((h, i) => {
        const value = rowValues[i] || "";
        rowData[h.name] = value;

        // 🔹 Required validation
        if (h.validation?.required && !value) {
          const msg = `Row ${rowNumber} - ${h.label} is required`;
          errorMessages.push(msg);
          newErrors[
            `capital_expenditure_details_through_bank_transactions.${newRows.length}.${h.name}`
          ] = msg;
        }

        if (
          h.validation?.pattern &&
          value &&
          !h.validation.pattern.test(value)
        ) {
          const msg = `Row ${rowNumber} - ${h.label} is invalid`;
          errorMessages.push(msg);
          newErrors[
            `capital_expenditure_details_through_bank_transactions.${newRows.length}.${h.name}`
          ] = msg;
        }
      });

      newRows.push(rowData);
    }

    // ❌ Block import on validation errors
    if (errorMessages.length > 0) {
      alert(
        "Import failed due to validation errors:\n\n" +
          errorMessages.join("\n"),
      );
      return;
    }

    if (newRows.length > 0) {
      const updatedRows = [];

      newRows.forEach((rowData, rowIndex) => {
        const newRow = {};
        headers.forEach((h) => {
          newRow[h.name] = rowData[h.name];
          onChange([...updatedRows, newRow], false, rowIndex, h.name);
        });
        updatedRows.push(newRow);
      });
      uploadCaptailExpenditureFile(file, "excel");
    } else {
      alert("Imported file has no valid data rows.");
    }
  };

  const uploadCaptailExpenditureFile = async (uploadedFile, type) => {
    try {
      if (!uploadedFile || !type) return;

      const name =
        type === "pdf"
          ? "capital_expenditure_details_pdf"
          : type === "excel"
            ? "capital_expenditure_details_excel"
            : "capital_expenditure_details_excel";
      const existingDocId = formData[name]?.id || "";
      const documentTypeId = formData[name]?.docTypeId || "";

      const fileFormData = new FormData();
      fileFormData.append("upload_doc", uploadedFile);
      fileFormData.append("upload_date", formatDate(new Date()));
      fileFormData.append("category_id", 1);
      fileFormData.append("document_type_id", documentTypeId);
      fileFormData.append("application_id", applicationId);
      fileFormData.append("upload_document_id", existingDocId);
      //fileFormData.append("user_id", userDetails.user_id);

      const uploadResponse = await api.post(
        "/application/document/upload",
        fileFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (uploadResponse?.success && uploadResponse?.data?.path) {
        if (type === "pdf") {
          setExpenditurePdfFileURL(uploadResponse.data.path);
          if (expenditureExcelFileURL) {
            setCapitalExpenditureDataImported(true);
          }
        } else if (type === "excel") {
          setExpenditureExcelFileURL(uploadResponse.data.path);
          if (expenditurePdfFileURL) {
            setCapitalExpenditureDataImported(true);
          }
        }
        setApiMessage({
          type: "success",
          message: "File uploaded successfully",
        });
        setFormData((prev) => ({
          ...prev,
          [name]: {
            url: uploadResponse.data.path,
            id: uploadResponse.data.id,
            name: uploadedFile.name,
            docTypeId: documentTypeId,
          },
        }));
      } else {
        setApiMessage({
          type: "error",
          message: "Error uploading file. Please try again.",
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      {apiMessage && (
        <Message apiMessage={apiMessage} setApiMessage={setApiMessage} />
      )}
      <div className={divClass}>
        {label && (
          <>
            {!isSpecialTable && <hr className="mt-2 mb-2" />}
            <p
              className={`text-mg font-semibold w-full mb-4 ${
                isSpecialTable ? "mb-4" : "px-2"
              }`}
            >
              {label}
            </p>
          </>
        )}
        {!isSpecialTable && (
          <table
            className={`${tableClass} ${
              tableDisabled ? "pointer-events-none opacity-50" : ""
            }`}
          >
            <thead className={headerBg || "bg-[#e5f5f4]"}>
              <tr>
                {!loanTables.includes(htmlFor) && (
                  <th
                    className={`${
                      headerClass ||
                      "border border-gray-400 p-2 text-center text-sm"
                    }`}
                    width="5%"
                  >
                    {language ? "क्रम संख्या" : "Sr.No."}
                  </th>
                )}
                {headers.map((header, idx) => (
                  <th
                    key={idx}
                    className={`${
                      loanTables ? "min-w-[150px]" : "min-w-[125px]"
                    } ${
                      headerClass ||
                      "border border-gray-400 p-2 text-center text-sm"
                    }`}
                    width={header.width}
                  >
                    {language ? header.label.hi : header.label.en}
                  </th>
                ))}
                {!isSpecialTable && canAddMore && (
                  <th className={headerClass}>
                    {language ? "कार्यवाही" : "Actions"}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {capitalExpenditureDataImported && isSpecialTable ? (
                <tr>
                  <td
                    colSpan={headers.length + 1} // Sr.No. + headers
                    className="text-center text-theme-color font-bold border border-gray-400 p-3"
                  >
                    {language
                      ? "डेटा इंपोर्ट हो गया"
                      : "Data Imported Successfully"}
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {!loanTables.includes(htmlFor) && (
                      <td
                        className={
                          rowClass ||
                          "border border-gray-400 p-2 text-gray-700 text-sm text-center"
                        }
                      >
                        {rowIndex + 1}
                      </td>
                    )}
                    {headers.map((header, colIndex) => {
                      const {
                        name,
                        type = "text",
                        placeholder,
                        maxLength,
                        options,
                        disabled = false,
                      } = header;
                      const fieldError =
                        errors[`${namePrefix}.${rowIndex}.${name}`] || null;

                      return (
                        <td
                          key={colIndex}
                          className={rowClass || "border border-gray-400 p-2"}
                        >
                          {type === "file" ? (
                            <div className="flex items-center justify-center gap-2 mt-2">
                              {/* ✅ Always allow re-upload on the left */}
                              <label className="px-3 py-2 bg-btn text-white text-sm rounded cursor-pointer hover:opacity-90 transition">
                                {language ? "फ़ाइल अपलोड करें" : "Uplaod File"}
                                <input
                                  type="file"
                                  accept={header.accept || "*"} // allow config-based types
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleInputChange(e, rowIndex, name);
                                    }
                                  }}
                                  className="hidden"
                                />
                              </label>
                              {row[name] && typeof row[name] === "string" && (
                                <a
                                  onClick={() => {
                                    window.open(
                                      `${BASE_URL}/${row[name]}`,
                                      "_blank",
                                    );
                                  }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-2 bg-btn text-white text-sm rounded cursor-pointer hover:opacity-90 transition"
                                >
                                  {language ? "फ़ाइल देखें" : "View File"}
                                </a>
                              )}
                            </div>
                          ) : isSpecialTable ? (
                            <span className="text-gray-700 text-sm">
                              {row[name] || ""}
                            </span>
                          ) : type === "select" ? (
                            <select
                              name={`${namePrefix}[${rowIndex}][${name}]`}
                              className="mt-1 w-full border rounded text-sm p-2"
                              value={row[name] || ""}
                              disabled={disabled}
                              onChange={(e) =>
                                handleInputChange(e, rowIndex, name)
                              }
                            >
                              <option value="">
                                {language ? "विकल्प चुनें" : "Select option"}
                              </option>
                              {options?.map((opt, i) => (
                                <option key={i} value={opt.value || opt}>
                                  {language ? opt.hi : opt.label || opt}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={type}
                              name={`${namePrefix}[${rowIndex}][${name}]`}
                              placeholder={placeholder || ""}
                              className="mt-1 w-full border rounded text-sm p-2"
                              value={row[name] || ""}
                              disabled={disabled}
                              maxLength={maxLength || 255}
                              onChange={(e) =>
                                handleInputChange(e, rowIndex, name)
                              }
                            />
                          )}
                          {fieldError && (
                            <p className="text-red-500 text-xs mt-1">
                              {language ? fieldError.hi : fieldError.en}
                            </p>
                          )}
                        </td>
                      );
                    })}
                    {!isSpecialTable && canAddMore && (
                      <td className="text-center border border-gray-400 p-2">
                        {rows?.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRow(rowIndex)}
                            className="text-red-500 text-xs"
                          >
                            {language ? "पंक्ति हटाएँ" : "Remove"}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* ✅ Special table buttons */}
        {isSpecialTable ? (
          <>
            <div>
              {language
                ? "प्रोत्साहन पात्रता के लिए निवेश विवरण जमा करने हेतु कृपया सभी चरणों का सावधानीपूर्वक पालन करें। प्रत्येक निवेशक को निवेशक व्यय शीट (सुरक्षित एक्सेल) डाउनलोड करनी होगी, सही तरीके से भरना होगा, सत्यापित करना होगा और उसी शीट को पोर्टल पर अपलोड करना होगा। अधिक जानकारी के लिए"
                : "To submit your investment details for incentive eligibility, please follow the steps carefully. Every investor must download the Investor Expenditure Sheet (Protected Excel), fill it correctly, validate it, and upload the same sheet on the portal. For more info"}
              <a
                href={ExependitureDetailInstruction}
                download
                className="text-theme-color hover:underline text-sm ml-2"
              >
                {language ? "(डाउनलोड करें)" : "(Download)"}
              </a>
            </div>

            {capitalExpenditureDataImported && (
              <div className="text-green-600 font-semibold mt-2">
                {language
                  ? "खर्च व्यय डेटा और पीडीएफ सफलतापूर्वक आयात हो गए हैं।"
                  : "Capital Expenditure data and Pdf has been imported successfully."}
              </div>
            )}

            {errors && !capitalExpenditureDataImported > 0 && (
              <div className="text-red-600 font-semibold mt-2">
                {language
                  ? "कृपया मान्य व्यय शीट और पीडीएफ फ़ाइल अपलोड करें।"
                  : "Please upload a valid Expenditure Sheet and Pdf file."}
              </div>
            )}

            <div className="flex gap-4 mt-2">
              <a
                href={InvestorExpenditureSheet}
                download
                className={`px-3 py-1 bg-blue-600 text-white text-sm rounded flex items-center ${
                  tableDisabled ? "pointer-events-none opacity-50" : ""
                }`}
                disabled={tableDisabled}
              >
                {language ? "टेम्पलेट डाउनलोड करें" : "Download Template"}
              </a>

              {/* Excel Upload */}
              <label
                className={`inline-flex items-center gap-2 px-3 py-2 ${
                  expenditureExcelFileURL ? "bg-green-600" : "bg-blue-600"
                } text-white text-sm rounded cursor-pointer ${
                  tableDisabled ? "opacity-50" : ""
                }`}
              >
                <span className="text-white">
                  {language ? "भरी हुई शीट अपलोड करें" : "Upload Filled Sheet"}
                </span>

                {expenditureExcelFileURL && (
                  <>
                    <span className="opacity-70 text-white">|</span>
                    <span
                      className="underline text-white"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(
                          `${BASE_URL}/${expenditureExcelFileURL}`,
                          "_blank",
                        );
                      }}
                    >
                      {language ? "देखें" : "View"}
                    </span>
                  </>
                )}

                <input
                  type="file"
                  accept=".xlsx"
                  onChange={importFile}
                  className="hidden"
                  disabled={tableDisabled}
                />
              </label>

              {/* PDF Upload */}
              <label
                className={`inline-flex items-center gap-2 px-3 py-2 ${
                  expenditurePdfFileURL ? "bg-green-600" : "bg-blue-600"
                } text-white text-sm rounded cursor-pointer ${
                  tableDisabled ? "opacity-50" : ""
                }`}
              >
                <span className="text-white">
                  {language ? "शीट अपलोड करें (पीडीएफ)" : "Upload Sheet (Pdf)"}
                </span>

                {expenditurePdfFileURL && (
                  <>
                    <span className="opacity-70 text-white">|</span>
                    <span
                      className="underline text-white"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(
                          `${BASE_URL}/${expenditurePdfFileURL}`,
                          "_blank",
                        );
                      }}
                    >
                      {language ? "देखें" : "View"}
                    </span>
                  </>
                )}

                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    uploadCaptailExpenditureFile(e.target.files[0], "pdf")
                  }
                  className="hidden"
                  disabled={tableDisabled}
                />
              </label>
            </div>
          </>
        ) : (
          canAddMore && (
            <button
              type="button"
              onClick={addRow}
              className={`mt-2 px-3 py-2 bg-btn text-white text-sm rounded ${
                tableDisabled ? "pointer-events-none opacity-50" : ""
              }`}
              disabled={tableDisabled}
            >
              {language ? "पंक्ति जोड़ें" : "Add Row"}
            </button>
          )
        )}
      </div>
    </>
  );
};

export default TableInput;
